import { useCallback, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { getSyncState, setSyncState } from '@/core/repository/syncStateRepository'
import type { SyncStatus } from './useSync'
import './sync.css'

interface SyncPanelProps {
  status: SyncStatus
  lastSyncAt: number | null
  onSyncNow: () => void
  onClose: () => void
}

const STATUS_TEXT: Record<SyncStatus, string> = {
  idle: 'Up to date',
  syncing: 'Syncing…',
  offline: 'Offline — changes are saved on this device',
  error: 'Could not reach the server',
  unauthorized: 'Token rejected',
}

/**
 * Sync settings: paste the token, see the status, force a sync.
 *
 * The token lives in syncState, never in the settings blob -- settings are
 * themselves synced, and round-tripping a credential through the API it
 * authenticates would be pointless and needlessly exposed.
 */
export function SyncPanel({ status, lastSyncAt, onSyncNow, onClose }: SyncPanelProps) {
  const storedToken = useLiveQuery(() => getSyncState('syncToken'), [], null)
  const [draft, setDraft] = useState('')
  const [saved, setSaved] = useState(false)

  const handleSave = useCallback(() => {
    const value = draft.trim()
    if (!value) return

    void setSyncState('syncToken', value).then(() => {
      setDraft('')
      setSaved(true)
      onSyncNow()
      setTimeout(() => setSaved(false), 2000)
    })
  }, [draft, onSyncNow])

  const handleForget = useCallback(() => {
    void setSyncState('syncToken', '')
  }, [])

  return (
    <div className="sy-sheet" role="dialog" aria-label="Sync settings">
      <header className="sy-head">
        <h2 className="sy-title">Sync</h2>
        <button className="sy-close" onClick={onClose} aria-label="Close">
          Done
        </button>
      </header>

      <div className="sy-body scroll-y">
        <div className="sy-status">
          <span className={`dot dot--${dotFor(status)}`} aria-hidden="true" />
          <span>{STATUS_TEXT[status]}</span>
        </div>

        {lastSyncAt ? (
          <p className="sy-muted">Last synced {relativeTime(lastSyncAt)}.</p>
        ) : null}

        {status === 'unauthorized' ? (
          <p className="sy-warn">
            The server rejected this token. Paste it again below, exactly as you set it with
            <code> wrangler secret put SYNC_TOKEN</code>.
          </p>
        ) : null}

        <section className="sy-section">
          <h3 className="sy-label">Sync token</h3>
          <p className="sy-muted">
            {storedToken
              ? 'A token is saved on this device.'
              : 'Paste the token you created for the server. The same one goes on both devices.'}
          </p>

          <input
            className="sy-input"
            type="password"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={storedToken ? 'Replace token' : 'Paste token'}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />

          <div className="sy-actions">
            <button className="sy-btn sy-btn--primary" onClick={handleSave} disabled={!draft.trim()}>
              {saved ? 'Saved' : 'Save token'}
            </button>
            {storedToken ? (
              <button className="sy-btn" onClick={handleForget}>
                Forget
              </button>
            ) : null}
          </div>
        </section>

        <section className="sy-section">
          <button className="sy-btn" onClick={onSyncNow} disabled={!storedToken}>
            Sync now
          </button>
          <p className="sy-muted">
            The app only syncs while it is open — Safari does not allow background sync. Open it on
            your other device and it will catch up.
          </p>
        </section>
      </div>
    </div>
  )
}

function dotFor(status: SyncStatus): 'ok' | 'warn' | 'error' | 'idle' {
  switch (status) {
    case 'idle':
      return 'ok'
    case 'syncing':
      return 'idle'
    case 'offline':
      return 'idle'
    case 'error':
      return 'warn'
    case 'unauthorized':
      return 'error'
  }
}

function relativeTime(ms: number): string {
  const seconds = Math.round((Date.now() - ms) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const hours = Math.round(minutes / 60)
  return `${hours} hour${hours === 1 ? '' : 's'} ago`
}
